import { FC } from "react";
import { ShareMessage } from "../action-buttons/ShareMessage";
import { DownloadMessage } from "../action-buttons/DownloadMessage";
import { useChatContext } from "../../ChatProvider";
import { ChatFeedbackActions } from "./ChatFeedbackActions";
import { ChatLastMessageActions } from "./ChatLastMessageActions";
import { CoachAI } from "../coachAI";

interface ChatMessageActionsProps {
  messageId: string;
  messageContent: string;
  sessionId?: string;
  isLastMessage: boolean;
}

export const ChatMessageActions: FC<ChatMessageActionsProps> = ({
  messageId,
  messageContent,
  sessionId,
  isLastMessage,
}) => {
  const { components } = useChatContext();

  // Allow custom feedback component override
  if (components?.feedback) {
    return components.feedback;
  }

  // Opt-in REPLACEMENT for the default Share button (AI-6343). Studio
  // injects a render function that returns StudioShareButton (opens full
  // ShareDialog with Link / Slack / Email tabs). Other surfaces (Summary,
  // Teammate) fall back to the default ShareMessage which opens EmailDialog.
  const renderShareButton =
    components?.shareButton ?? (({ sessionId: sid }) => <ShareMessage sessionId={sid} />);

  return (
    <div
      role="group"
      aria-label="Message actions"
      className="al-mt-4 al-pt-4 al-border-t al-border-gray-200"
    >
      {/* Flex container with wrap to allow feedback form on next line */}
      <div className="al-flex al-flex-row al-flex-wrap al-items-center al-justify-between al-gap-2">
        {/* Left group: Share and Download */}
        <div
          className="al-flex al-flex-row al-items-center al-gap-1"
          role="group"
          aria-label="Message share and download"
        >
          {renderShareButton({ sessionId })}
          <DownloadMessage sessionId={sessionId} />
          {components?.messageActions?.({ sessionId, messageId, isLastMessage })}
          {isLastMessage && <CoachAI />}
        </div>

        {/* Right group: Regenerate, Copy, Feedback buttons */}
        <ChatFeedbackActions messageId={messageId}>
          {isLastMessage && (
            <ChatLastMessageActions messageId={messageId} content={messageContent} />
          )}
        </ChatFeedbackActions>
      </div>
    </div>
  );
};
